import {wrapError} from '@northpoint-development/labs-common-core';

import {MapService} from '@/services/MapService';
import {getStore} from '@/services/store';
import {getDocument, updateDocument} from '@/services/FirestoreService';
import {getCollection} from '@/lib/filters/filters';
import {collectionNameByFirestoreCollection} from '@/internal/store/collections';
import {KC_OFFICE_LOCATION} from '@/services/BulkUploadService';
import {revealCompetitiveSection, placeStagingMarker} from '@/services/OMService';

import {bulkConfig} from './config';
import type {StagedRecord} from './types';

/**
 * Pans to an already-committed record and opens its read-only competitive info window.
 * Unlike OM's approved branch (which highlights the staging marker), bulk removes the staging
 * marker on approval, so this points at the committed marker and guards against a deleted record.
 */
async function focusCommittedRecord(item: StagedRecord): Promise<void> {
  const cfg = bulkConfig[item.kind];
  const committedId = item[cfg.finalIdKey];
  if (!committedId) return;

  const filterCollection = collectionNameByFirestoreCollection[cfg.finalFirestoreCollection];
  const committed = await getDocument(cfg.finalFirestoreCollection, committedId, true);

  // The committed record can be deleted after approval (soft-delete leaves the doc with a
  // `deleted` timestamp). Don't pan to / open a record that no longer exists.
  if (!committed || committed.deleted) {
    getStore().commit('setSnack', {message: `This ${item.kind} has been deleted.`, color: 'error'});
    return;
  }

  const filterItem = getCollection(filterCollection)?.getItem(committedId);

  revealCompetitiveSection(item.kind);

  const loc = committed?.location ?? item.location;
  if (loc) {
    await MapService.centerViewport({lat: loc.latitude, lng: loc.longitude}, 15);
  }

  getStore().dispatch(`infowindow/${cfg.finalInfoWindowAction}`, {
    subject: {
      ...filterItem,
      ...committed,
      // The committed Firestore doc has no marker data; reuse the map marker's, or synthesize it.
      markerData: filterItem?.markerData ?? {collection: filterCollection, id: committedId, icon: cfg.markerIcon},
      firestoreCollection: cfg.finalFirestoreCollection,
      filterCollection,
    },
  });
}

/**
 * Places the in-review staging marker and opens its editable review info window
 * (Approve/Delete). Mirrors `placeMarkerForOM` with the flat/dotless icon. Already-approved
 * records instead pan to their committed marker and open the read-only committed window.
 */
async function placeBulkMarker(item: StagedRecord): Promise<void> {
  try {
    if (item.status === 'approved') {
      await focusCommittedRecord(item);
      return;
    }

    const cfg = bulkConfig[item.kind];
    const location = item.location ?? KC_OFFICE_LOCATION;
    if (!item.location) {
      getStore().commit('setSnack', {message: 'No location was found for this record.'});
    }

    const dragend = {
      event: 'dragend',
      callback: async (e: {lat: number; lng: number}) => {
        await updateDocument(cfg.stagingCollection, item.id, {location: {latitude: e.lat, longitude: e.lng}});
      },
    };

    revealCompetitiveSection(item.kind);
    await placeStagingMarker(cfg.localCollection, {
      type: cfg.fullType,
      id: item.id,
      name: item.name,
      location,
      icon: cfg.markerIcon,
      events: [dragend],
    });

    getStore().dispatch('infowindow/createBulkReviewTab', {subject: {...item, location}});
  } catch (error) {
    throw wrapError(error, `Error placing bulk ${item.kind} on map`);
  }
}

export {placeBulkMarker};
